import { Position, Direction } from '../core/GridGeometry.js';

/** Delay before the delete cursor (red X) appears, so a quick `C` tap that
 *  deletes one cell doesn't flash the indicator red. */
const DELETE_CURSOR_DELAY_MS = 140;

/** One cursor move during a trace, handed to {@link KeyboardControllable.traceStep}. */
export interface TraceStep {
    /** The cell the cursor just left. */
    from: Position;
    /** The cell the cursor moved into (the current cursor cell). */
    to: Position;
    /** The direction of this one-cell move. */
    moveDir: Direction;
}

/**
 * How a game answers a direction press during a trace (see
 * {@link KeyboardControllable.traceDirPress}):
 *  - `'move'` — step normally (the default when the hook isn't implemented)
 *  - `'blocked'` — refuse the step; the cursor stays put and nothing changes
 *  - `'handled'` — the game acted on the press itself; the cursor stays put and
 *    the board counts as changed (Antennas aiming an emitter)
 */
export type TraceDirResult = 'move' | 'blocked' | 'handled';

/**
 * A point on the half-resolution grid, at twice the cell resolution. `r` and `c`
 * run 1..2·size-1; their parity gives the kind of point:
 *  - both odd → a cell **center** (cell = ((r-1)/2, (c-1)/2))
 *  - one odd, one even → an **edge** midpoint
 *  - both even → an **intersection** (grid corner)
 * Used by fine-cursor games (Walls) that place on edges. See {@link KeyboardControllable.usesFineCursor}.
 */
export interface FinePoint { r: number; c: number; }

/** One cell-to-cell move during a fine-cursor trace, at the cell being left. */
export interface FineTraceStep {
    /** The anchor cell the trace is leaving. */
    cell: Position;
    /** The block's incoming direction (null on the first cell move — no anchor). */
    fromDir: Direction | null;
    /** The direction of this cell-to-cell move. */
    moveDir: Direction;
}

/**
 * A game input that supports keyboard-driven placement. Games opt in by
 * implementing this on their Input class; the KeyboardController dispatches the
 * cursor's cell to it. Mirrors the click / right-click semantics.
 *
 * Trace support is optional and game-defined: the controller only reports the
 * gesture (a hold began, the cursor stepped) and lets the game decide what a
 * trace *means* — Mirrors lays mirrors along the beam's turns, Knight snaps a
 * segment in when the cursor reaches a reachable knight cell.
 */
export interface KeyboardControllable {
    /**
     * Spacebar (on key-up, tapped): place or cycle the element at the cursor
     * cell, matching the primary click. Returns true if the board changed.
     * Cell-cursor games implement this; fine-cursor games use {@link placeAtFine}.
     */
    placeAtCursor?(pos: Position): boolean;
    /**
     * `C`: delete the element at the cursor cell, matching the remove action.
     * Returns true if the board changed. Cell-cursor games implement this;
     * fine-cursor games use {@link deleteAtFine}.
     */
    deleteAtCursor?(pos: Position): boolean;
    /**
     * Trace support (optional): a trace just began, anchored at `pos` (the cursor
     * cell when the spacebar went down). Called once, on the first move with the
     * spacebar held — never for a plain tap.
     */
    traceStart?(pos: Position): void;
    /**
     * Trace support (optional): the cursor stepped one cell while tracing. Act on
     * it however the game defines; return true if the board changed.
     */
    traceStep?(step: TraceStep): boolean;
    /**
     * Trace support (optional): with the spacebar held, the game vets each direction
     * press before the cursor moves — see {@link TraceDirResult}. Antennas uses it
     * both to aim (`'handled'`) and to keep the cursor pinned to the antenna it's
     * drawing (`'blocked'`). Omitting it means every step just moves.
     */
    traceDirPress?(pos: Position, dir: Direction): TraceDirResult;
    /**
     * Trace support (optional): the spacebar was released, ending a cell trace.
     * Commit whatever the trace built (Antennas finishes the antenna, placing its
     * emitter); return true if the board changed. Games whose trace commits on
     * every step (Mirrors, Splitting, …) don't implement this.
     */
    traceEnd?(): boolean;
    /**
     * Optional: where `X` sends the cursor — the spot play naturally starts from.
     * Game-defined: the top-left cell for Mirrors, the first digit for Knight, the
     * stem for Branches, and so on. Used by cell- and point-cursor games (both are
     * `Position`); fine-cursor games answer with {@link fineCursorOrigin} instead.
     * Return null to leave the cursor where it is.
     */
    cursorOrigin?(): Position | null;
    /** {@link cursorOrigin} for fine-cursor games, on the half-grid. */
    fineCursorOrigin?(): FinePoint | null;
    /**
     * Optional: allow the cursor to step one cell outside the grid onto the rim
     * ring (e.g. Branches, to place the stem). The corners (both axes out) are
     * always excluded. Defaults to false — the cursor stays on interior cells.
     */
    allowsRimCursor?(): boolean;
    /**
     * Optional: whether the plain cell cursor wraps toroidally at the edges outside a
     * trace. Defaults to true. Jumps opts out — its board is four quarters with hard
     * boundaries, so running off one side and back on the other means nothing there.
     */
    wrapsCellCursor?(): boolean;

    // ── Fine (half-grid) cursor: edge placement, e.g. Walls ────────────
    /** Opt into the half-resolution cursor that visits edges/centers/intersections. */
    usesFineCursor?(): boolean;
    /** Spacebar tap: place at a fine point (Walls: toggle a wall when on an edge). */
    placeAtFine?(p: FinePoint): boolean;
    /** `C`: delete at a fine point (Walls: remove a wall when on an edge). */
    deleteAtFine?(p: FinePoint): boolean;
    /** A cell-to-cell trace move; place/replace the turning element at `cell`. */
    traceFine?(step: FineTraceStep): boolean;
    /**
     * Optional (fine cursor): veto a whole-cell trace hop out of `cell` heading
     * `moveDir` — the cursor stays put and nothing is laid. Ignition uses it to stop
     * a fuse trace running onto a digit cell. The fine counterpart of the cell
     * cursor's `'blocked'` {@link traceDirPress} answer.
     */
    blocksFineTrace?(cell: Position, moveDir: Direction): boolean;
    /**
     * Optional (fine cursor): extend the half-grid to the outer boundary, so the
     * cursor can also sit on **rim** edges. Orbits needs it — its exit gates live on
     * the grid boundary. Defaults to false, keeping the cursor on interior edges
     * (Walls, where a rim edge can't hold a wall).
     */
    allowsFineRim?(): boolean;
    /**
     * Optional (fine cursor): whether the cursor wraps toroidally at the grid edges.
     * Defaults to true — Walls' sliding block wraps, so its cursor does too. Orbits
     * opts out: its ship leaves through an exit gate rather than reappearing on the
     * far side, so the cursor stops dead at the boundary.
     */
    wrapsFineCursor?(): boolean;
    /**
     * Optional (fine cursor): anchor the trace on grid **intersections** rather than
     * cell centers, hopping intersection-to-intersection and acting on the edge
     * crossed in between (via {@link traceFineEdge}). Flood's walls run *along* the
     * grid lines, so that's how you draw them; Walls' arrows redirect a block moving
     * cell to cell, so it keeps the default centers.
     */
    tracesFineIntersections?(): boolean;
    /** An intersection-to-intersection trace hop: act on `edge`, the half-grid point
     *  of the edge crossed between them (Flood lays a wall). Returns changed. */
    traceFineEdge?(edge: FinePoint, moveDir: Direction): boolean;
    /**
     * Opt into a **toroidal** intersection trace (Line): each hop toggles the segment
     * crossed via {@link toggleSegment} (in intersection `Position`s), and reaching the
     * rim also toggles its wrap-mirror on the far edge and lands there — drawing a
     * line that wraps the grid in one sweep. The stopping case is {@link traceReachesEnd}.
     * Overrides {@link traceFineEdge}/{@link traceFinePoint} for the hop.
     */
    tracesToroidal?(): boolean;
    /**
     * Optional (fine cursor): a fine trace just anchored at `p` — the half-grid
     * analogue of {@link traceStart}. Fired when the spacebar goes down on an anchor
     * point, and again whenever the cursor settles onto a fresh one.
     */
    traceFineStart?(p: FinePoint): void;
    /**
     * Optional (fine cursor): a trace hop reached the point `to`. The alternative to
     * {@link traceFineEdge} for games that **snap** rather than act on what they
     * crossed — Deluge watches for the cursor landing diagonally from its anchor and
     * drops the wall between, Knight-style. Returns true if the board changed.
     */
    traceFinePoint?(to: FinePoint): boolean;
    /**
     * Optional (fine cursor, with {@link allowsFineRim}): the **top rim** row is a row
     * of slots rather than ordinary edges — Flood's faucet. Sliding along it hops
     * between slot midpoints, skipping the intersections, and the cursor is drawn as
     * a rim marker. The intersections stay reachable by stepping up from below.
     */
    fineTopRimSlots?(): boolean;
    /**
     * Optional (fine cursor, with {@link allowsFineRim}): a trace may **start on a
     * rim edge and step inward**. The hop is reported through {@link traceFine} with
     * `cell` set to the notional cell just outside the rim, so the game sees an
     * ordinary hop that happens to cross the boundary — the mirror of running *off*
     * the grid, which reports the inside cell and an outward `moveDir`.
     *
     * Branches uses it: its stem anchors to a rim edge, so tracing in from outside
     * plants the stem and carries straight on into the tree, instead of making you
     * tap the rim first and start a second gesture.
     */
    tracesFromFineRim?(): boolean;
    /**
     * Optional: whether to draw the blue "last step" hint segment during a trace.
     * Defaults to false, for every cursor model. Worth turning on when each step *is*
     * a placement **and** that placement has a heading the arrow can point along —
     * Mirrors' bends, Walls' arrows, Orbits' gates. Off for steps that don't map 1:1
     * to a piece (Knight's knight-moves) and for pieces with no direction to them at
     * all (Flood's walls, Ignition's fuses), where the arrow is just noise.
     */
    wantsTraceHint?(): boolean;

    // ── Constrained trace: anchor + confined cursor, commit on release (Crossing) ──
    /**
     * Opt into a constrained trace: the spacebar anchors a first piece, the cursor
     * is then confined to game-chosen targets, and the pair is committed on release
     * rather than per step.
     */
    usesConstrainedTrace?(): boolean;
    /** Whether a half-placement is pending (Crossing: a first teleporter is down).
     *  While true, the space gesture completes it instead of starting a trace. */
    traceHasPending?(): boolean;
    /** Whether a constrained trace may anchor at `pos` (else it waits for a valid cell). */
    traceCanAnchor?(pos: Position): boolean;
    /** Anchor a constrained trace at `pos` (Crossing: place the pending first teleporter). */
    traceAnchor?(pos: Position): void;
    /** Whether `pos` is a legal cursor target for a trace anchored at `anchor`
     *  (Crossing: a valid second-teleporter cell). */
    isTraceTarget?(anchor: Position, pos: Position): boolean;
    /** Commit a constrained trace on release (Crossing: complete the pair at `current`).
     *  Returns true if the board changed. */
    traceCommit?(anchor: Position, current: Position): boolean;

    // ── Point cursor: rides grid intersections, e.g. Line ──────────────
    /** Opt into a cursor that rides grid intersection points [0..size]², not cells. */
    usesPointCursor?(): boolean;
    /** Spacebar tap: act at an intersection (Line: set the rim start point). */
    placeAtPoint?(p: Position): boolean;
    /** `C`: act at an intersection (Line: remove the segments touching it). */
    deleteAtPoint?(p: Position): boolean;
    /** Toggle the line segment between two adjacent intersections. Returns changed.
     *  The trace calls this as the cursor steps (twice at once across the rim). */
    toggleSegment?(a: Position, b: Position): boolean;
    /** Whether a segment is currently placed between two adjacent intersections —
     *  lets the toroidal trace tell an *erase* from an *extend* at the rim. */
    hasSegment?(a: Position, b: Position): boolean;
    /** Whether the line currently runs home to the rim (Line: trace reachedEnd). When
     *  a rim step brings it home, the trace stops there instead of drawing the wrap
     *  mirror — an extra segment past the finish would only be a stray. */
    traceReachesEnd?(): boolean;
}

export function isKeyboardControllable(x: unknown): x is KeyboardControllable {
    if (!x) return false;
    const k = x as KeyboardControllable;
    // A cell-cursor game exposes placeAtCursor; the alternative cursor models opt in
    // via their own flag. Any of these marks the input as keyboard-controllable.
    return typeof k.placeAtCursor === 'function' ||
        typeof k.usesFineCursor === 'function' ||
        typeof k.usesPointCursor === 'function' ||
        typeof k.usesConstrainedTrace === 'function';
}

export interface KeyboardControllerCallbacks {
    /** Whether we're in live play (not a modal/tutorial/hub). Gates the shortcuts. */
    isInGame(): boolean;
    /** Current grid size; the cursor is clamped to [0, size - 1]. */
    getGridSize(): number;
    /** The active game input, dispatched to for place/delete/trace. */
    getActiveInput(): unknown;
    /** Cursor moved or mode toggled: re-render (the app pushes the cursor to the
     *  renderer). */
    onChange(): void;
    /** Mode toggled via K: persist + reflect in an open settings panel. */
    onToggle(enabled: boolean): void;
    /** A place/delete/trace changed the board: save + refresh. */
    onBoardChange(): void;
    /** `B`: restore the best solution for the current game. */
    onRestoreBest(): void;
    /** `N`: jump to the next unsolved game. */
    onNextGame(): void;
    /** `R`: reset the current game. */
    onReset(): void;
    /** `V`: toggle Activate Vortex mode (no-op off a Vortex grid). */
    onToggleGateMode(): void;
    /** `H`: enter or leave the Vortex Hub (no-op off a Vortex grid). */
    onToggleHub(): void;
}

/**
 * Shared keyboard-play controller: owns the on/off mode, the crosshair cursor
 * position, and the window-level key listeners. Cross-cutting (one instance for
 * the whole app); the active game's renderer draws the cursor and its input
 * handles placement (see {@link KeyboardControllable}).
 *
 * Spacebar has two modes: a plain tap places at the cursor (on key-up), while
 * holding it and moving *traces* — each turn drops the element needed to send
 * the game's path where the cursor went. The cursor shows a crosshair for place
 * mode and a corner-bracket reticle once a trace is under way.
 */
export class KeyboardController {
    private enabled = false;
    /** Null until the mode is first enabled (then centered on the grid). */
    private cursor: Position | null = null;
    /** Where the cursor sat before stepping onto another surface (the Vortex Hub),
     *  so leaving it puts the cursor back rather than stranding it. */
    private stashedCursor: Position | null = null;
    /** Spacebar is held (a tap places on key-up; a hold-and-move traces). */
    private spaceHeld = false;
    /** True once the cursor has moved with space held — a trace is under way. */
    private tracing = false;
    /** The cell the last trace step came from (cell mode), for the hint segment. */
    private cellTraceFrom: Position | null = null;
    /** `C` is held (a tap deletes one cell; a hold-and-move sweeps deletes). */
    private cHeld = false;
    /** Whether the delete cursor (red X) is showing — set after the tap delay or
     *  the first move, so a quick tap never flashes it. */
    private cDeleting = false;
    /** Pending timer that flips {@link cDeleting} on once `C` is held past the delay. */
    private cDeleteTimer: number | null = null;

    // Fine (half-grid) cursor state, used only when the active input opts in.
    /** Half-grid cursor point, or null before first use. */
    private fine: FinePoint | null = null;
    /** Reticle showing — the fine trace is active on a cell center. Set as soon as
     *  the spacebar goes down on a center, so the reticle appears straight away. */
    private fineTraceActive = false;
    /** The fine cursor actually moved with the spacebar held — i.e. this is a real
     *  trace, not a tap. Distinct from {@link fineTraceActive}, which is up from the
     *  moment space goes down; without it a tap on a cell center would never place. */
    private fineTraceMoved = false;
    /** Direction of the last completed cell-to-cell move (the block's dir into the anchor). */
    private fineTraceDir: Direction | null = null;
    /** The center the last fine hop came from, for the hint segment (null on wrap). */
    private fineTraceFrom: FinePoint | null = null;

    // Constrained-trace state (Crossing).
    /** A constrained trace is anchored and active (cursor confined to targets). */
    private ctActive = false;
    /** The anchored first piece of a constrained trace. */
    private ctAnchor: Position | null = null;

    /** Point cursor on grid intersections [0..size]² (Line), or null before use. */
    private point: Position | null = null;

    private readonly cb: KeyboardControllerCallbacks;

    constructor(cb: KeyboardControllerCallbacks) {
        this.cb = cb;
        window.addEventListener('keydown', this.handleKeyDown);
        window.addEventListener('keyup', this.handleKeyUp);
    }

    isEnabled(): boolean {
        return this.enabled;
    }

    /** True while a trace is under way (drives the reticle cursor shape). */
    isTracing(): boolean {
        if (!this.enabled) return false;
        if (this.fineMode()) return this.fineTraceActive;
        if (this.constrainedMode()) return this.ctActive;
        return this.tracing;
    }

    /** True while a delete sweep is showing (drives the red X cursor shape). */
    isDeleting(): boolean {
        return this.enabled && this.cDeleting;
    }

    /** The cell cursor for the renderer, or null (off, or in fine/point-cursor mode).
     *  Re-clamps to the interior if a game switch left it on a now-illegal rim. */
    getCursor(): Position | null {
        if (!this.enabled || this.fineMode() || this.pointMode() || !this.cursor) return null;
        if (!this.isValidCursor(this.cursor)) {
            this.cursor = this.clampInterior(this.cursor);
        }
        return { ...this.cursor };
    }

    /** The intersection-point cursor for the renderer, or null when off / not in point mode. */
    getPointCursor(): Position | null {
        if (!this.enabled || !this.pointMode()) return null;
        if (!this.point || !this.isValidPoint(this.point)) this.point = this.centerPoint();
        return { ...this.point };
    }

    /** The half-grid cursor for the renderer, or null when off / not in fine mode. */
    getFineCursor(): FinePoint | null {
        if (!this.enabled || !this.fineMode()) return null;
        if (!this.fine || !this.isValidFine(this.fine)) this.fine = this.centerFine();
        return { ...this.fine };
    }

    /** The cell the last trace step came from — for the hint segment — or null when
     *  not mid cell-trace. */
    getCursorTraceFrom(): Position | null {
        if (!this.enabled || this.fineMode() || !this.tracing || !this.cellTraceFrom) return null;
        if (!this.input()?.wantsTraceHint?.()) return null;
        return { ...this.cellTraceFrom };
    }

    /** The center the last fine hop came from — for the hint segment — or null. */
    getFineTraceFrom(): FinePoint | null {
        if (!this.enabled || !this.fineMode() || !this.fineTraceActive || !this.fineTraceFrom) return null;
        if (!this.input()?.wantsTraceHint?.()) return null;
        return { ...this.fineTraceFrom };
    }

    /**
     * Turn keyboard mode on/off (settings toggle or K). On enable, the cursor
     * is centered on first use and re-clamped thereafter (in case the grid
     * shrank). Does not persist — the caller owns persistence.
     */
    setEnabled(enabled: boolean): void {
        if (enabled === this.enabled) return;
        this.enabled = enabled;
        this.resetTrace();
        this.clearDeleteState();
        if (enabled) {
            const n = this.cb.getGridSize();
            this.cursor = this.cursor
                ? this.clampInterior(this.cursor)
                : { row: Math.floor(n / 2), col: Math.floor(n / 2) };
        }
        this.cb.onChange();
    }

    /**
     * Send the cursor back to the active game's origin (`X`, and whenever the game
     * is reset — a blank board should start you where play begins).
     */
    resetCursor(): void {
        if (!this.enabled) return;
        this.jumpToOrigin();
    }

    /**
     * Moving to another surface (the Vortex Hub): remember where the cursor was and
     * send it to the incoming surface's origin. The caller must switch the active
     * input over *first*, so the origin comes from the surface being entered.
     */
    stashCursor(): void {
        if (!this.enabled) return;
        this.stashedCursor = this.cursor ? { ...this.cursor } : null;
        this.jumpToOrigin();
    }

    /** Coming back from that surface: put the cursor back where it was. */
    restoreCursor(): void {
        if (!this.enabled) return;
        this.resetTrace();
        this.clearDeleteState();
        if (this.stashedCursor && this.isValidCursor(this.stashedCursor)) {
            this.cursor = { ...this.stashedCursor };
        }
        this.stashedCursor = null;
        this.cb.onChange();
    }

    destroy(): void {
        window.removeEventListener('keydown', this.handleKeyDown);
        window.removeEventListener('keyup', this.handleKeyUp);
    }

    // ── Internals ──────────────────────────────────────────────────────

    private resetTrace(): void {
        this.spaceHeld = false;
        this.tracing = false;
        this.cellTraceFrom = null;
        this.fineTraceActive = false;
        this.fineTraceMoved = false;
        this.fineTraceDir = null;
        this.fineTraceFrom = null;
        this.ctActive = false;
        this.ctAnchor = null;
    }

    private clearDeleteState(): void {
        if (this.cDeleteTimer !== null) {
            window.clearTimeout(this.cDeleteTimer);
            this.cDeleteTimer = null;
        }
        this.cHeld = false;
        this.cDeleting = false;
    }

    private clampInterior(p: Position): Position {
        const n = this.cb.getGridSize();
        return {
            row: Math.max(0, Math.min(n - 1, p.row)),
            col: Math.max(0, Math.min(n - 1, p.col)),
        };
    }

    /**
     * Whether the cursor may occupy `p`: any interior cell, plus — when the active
     * input opts into the rim — the ring one cell outside each edge, excluding the
     * four outer corners (both axes out).
     */
    private isValidCursor(p: Position): boolean {
        const n = this.cb.getGridSize();
        const rowIn = p.row >= 0 && p.row < n;
        const colIn = p.col >= 0 && p.col < n;
        if (rowIn && colIn) return true;                 // interior cell
        if (!this.input()?.allowsRimCursor?.()) return false;
        const rowRim = (p.row === -1 || p.row === n) && colIn;
        const colRim = (p.col === -1 || p.col === n) && rowIn;
        return rowRim || colRim;                          // rim ring, no corners
    }

    private static dirOf(from: Position, to: Position): Direction {
        if (to.row < from.row) return 'N';
        if (to.row > from.row) return 'S';
        if (to.col < from.col) return 'W';
        return 'E';
    }

    private input(): KeyboardControllable | null {
        const input = this.cb.getActiveInput();
        return isKeyboardControllable(input) ? input : null;
    }

    private fineMode(): boolean {
        return !!this.input()?.usesFineCursor?.();
    }

    private constrainedMode(): boolean {
        return !!this.input()?.usesConstrainedTrace?.();
    }

    private pointMode(): boolean {
        return !!this.input()?.usesPointCursor?.();
    }

    /** Route an arrow step to the mover for the active game's cursor model. */
    private step(dRow: number, dCol: number): void {
        if (this.fineMode()) {
            if (!this.cHeld && this.spaceHeld && this.fineTraceActive &&
                this.fine && this.isFineAnchorPoint(this.fine)) {
                // Mid-trace on an anchor: jump a whole step to the next one.
                if (this.fineAnchorsOnIntersections()) this.moveFineIntersection(dRow, dCol);
                else this.moveFineCell(dRow, dCol);
            } else {
                this.moveFine(dRow, dCol);
            }
            return;
        }
        if (this.pointMode()) { this.stepPoint(dRow, dCol); return; }
        if (this.constrainedMode()) { this.stepConstrained(dRow, dCol); return; }

        // With the spacebar held, the game vets the direction before the cursor
        // moves — it may act on it itself (Antennas' aim mode) or refuse it. Either
        // way the press is part of the gesture, so mark the trace under way: the
        // release must not then also place.
        if (this.spaceHeld && !this.cHeld && this.cursor) {
            const dir: Direction = dRow < 0 ? 'N' : dRow > 0 ? 'S' : dCol < 0 ? 'W' : 'E';
            const res = this.input()?.traceDirPress?.({ ...this.cursor }, dir);
            if (res === 'handled' || res === 'blocked') {
                this.tracing = true;
                if (res === 'handled') this.cb.onBoardChange();
                return;
            }
        }

        this.move(dRow, dCol);
    }

    /**
     * Constrained-trace movement (Crossing). While the trace is active the cursor
     * is confined to the game's targets (valid second-teleporter cells); otherwise
     * it moves freely and — with the spacebar held and nothing pending — anchors as
     * soon as it lands on a valid cell.
     */
    private stepConstrained(dRow: number, dCol: number): void {
        if (!this.cHeld && this.ctActive) {
            this.moveConstrained(dRow, dCol);
        } else {
            this.move(dRow, dCol);
            if (this.spaceHeld && !this.cHeld) this.tryAnchor();
        }
    }

    /** Anchor a constrained trace at the cursor if allowed (valid cell, nothing pending). */
    private tryAnchor(): void {
        if (this.ctActive || !this.cursor) return;
        const input = this.input();
        if (!input?.usesConstrainedTrace?.() || input.traceHasPending?.()) return;
        if (input.traceCanAnchor?.(this.cursor)) {
            input.traceAnchor?.({ ...this.cursor });
            this.ctAnchor = { ...this.cursor };
            this.ctActive = true;
            this.cb.onChange(); // show the pending piece + its valid targets (transient, no save)
        }
    }

    /** Move the cursor within a constrained trace: jump to the next legal target
     *  along the anchor's row or column (skipping invalid cells, e.g. where another
     *  pair's line crosses), retract back toward/onto the anchor, restart from the
     *  anchor on an orthogonal turn, and refuse to step past the last target. */
    private moveConstrained(dRow: number, dCol: number): void {
        if (!this.cursor || !this.ctAnchor) return;
        const anchor = this.ctAnchor;
        const cur = this.cursor;
        const isAnchor = (p: Position) => p.row === anchor.row && p.col === anchor.col;

        let next: Position | null;
        if (isAnchor(cur)) {
            next = this.scanTarget(cur, dRow, dCol, false); // leave the anchor onto a target
        } else {
            const curAxisH = cur.row === anchor.row; // cursor sits on the anchor's row
            const moveAxisH = dRow === 0;            // moving along a row
            if (curAxisH === moveAxisH) {
                // Same axis: jump to the next target outward, or retract onto the anchor.
                next = this.scanTarget(cur, dRow, dCol, true);
            } else {
                // Orthogonal: restart from the anchor, then to the first target that way.
                next = this.scanTarget(anchor, dRow, dCol, false) ?? { ...anchor };
            }
        }

        if (next && (next.row !== cur.row || next.col !== cur.col)) {
            this.cursor = next;
            this.cb.onChange();
        }
    }

    /** Scan from `from` along (dRow, dCol) for the nearest constrained-trace target,
     *  jumping over invalid cells. Returns the anchor if reached first (when
     *  `stopAtAnchor`), or null at the grid edge. A digit/teleporter blocks because
     *  no target ever lies beyond it. */
    private scanTarget(from: Position, dRow: number, dCol: number, stopAtAnchor: boolean): Position | null {
        const n = this.cb.getGridSize();
        const anchor = this.ctAnchor!;
        const input = this.input();
        let r = from.row + dRow;
        let c = from.col + dCol;
        while (r >= 0 && r < n && c >= 0 && c < n) {
            const p: Position = { row: r, col: c };
            if (stopAtAnchor && p.row === anchor.row && p.col === anchor.col) return p;
            if (input?.isTraceTarget?.(anchor, p)) return p;
            r += dRow;
            c += dCol;
        }
        return null;
    }

    /** End a constrained trace on spacebar release: commit the pair if a second
     *  target is selected, else fall back to a tap (complete a pending / place). */
    private endConstrainedTrace(): void {
        const wasActive = this.ctActive;
        const anchor = this.ctAnchor;
        const cur = this.cursor;
        this.resetTrace();
        if (wasActive && anchor && cur && (cur.row !== anchor.row || cur.col !== anchor.col)) {
            if (this.input()?.traceCommit?.(anchor, { ...cur })) this.cb.onBoardChange();
        } else if (!wasActive) {
            this.doPlace(); // tap: complete a pending pair, place a first, toggle, …
        }
        // wasActive but cursor still on the anchor: leave the pending first piece as-is.
        this.cb.onChange();
    }

    // ── Point cursor (Line): rides grid intersections ──────────────────

    private centerPoint(): Position {
        const mid = Math.round(this.cb.getGridSize() / 2);
        return { row: mid, col: mid };
    }

    private isValidPoint(p: Position): boolean {
        const n = this.cb.getGridSize();
        return p.row >= 0 && p.row <= n && p.col >= 0 && p.col <= n;
    }

    private isRimPoint(p: Position): boolean {
        const n = this.cb.getGridSize();
        return p.row === 0 || p.row === n || p.col === 0 || p.col === n;
    }

    /** A segment between two adjacent points that runs along the outer rim — never
     *  a legal Line segment. */
    private isRimEdge(a: Position, b: Position): boolean {
        const n = this.cb.getGridSize();
        return (a.row === b.row && (a.row === 0 || a.row === n)) ||
            (a.col === b.col && (a.col === 0 || a.col === n));
    }

    /** The opposite rim point for a toroidal wrap (edge rim points only). */
    private wrapPoint(p: Position): Position {
        const n = this.cb.getGridSize();
        if (p.row === 0) return { row: n, col: p.col };
        if (p.row === n) return { row: 0, col: p.col };
        if (p.col === 0) return { row: p.row, col: n };
        return { row: p.row, col: 0 };
    }

    private stepPoint(dRow: number, dCol: number): void {
        if (!this.point) this.point = this.centerPoint();
        if (this.spaceHeld && !this.cHeld) {
            this.tracing = true;
            this.movePointTrace(dRow, dCol);
        } else {
            this.movePointFree(dRow, dCol);
        }
    }

    /** Free point movement (place / delete): step between intersections, clamped to
     *  the grid (so the rim stays reachable for the start point). */
    private movePointFree(dRow: number, dCol: number): void {
        const n = this.cb.getGridSize();
        const from = this.point!;
        const to: Position = {
            row: Math.max(0, Math.min(n, from.row + dRow)),
            col: Math.max(0, Math.min(n, from.col + dCol)),
        };
        if (to.row === from.row && to.col === from.col) return;
        this.point = to;
        if (this.cHeld) {
            this.showDeleteCursor();
            if (this.input()?.deleteAtPoint?.({ ...to })) this.cb.onBoardChange();
        }
        this.cb.onChange();
    }

    /** Trace point movement: each step toggles the segment between intersections.
     *  Reaching the rim toggles two segments at once — the one into the rim and its
     *  wrap-mirror on the opposite side — and lands the cursor on the far interior
     *  point, so a toroidal line is drawn in one continuous sweep. */
    private movePointTrace(dRow: number, dCol: number): void {
        const from = this.point!;
        const dest: Position = { row: from.row + dRow, col: from.col + dCol };
        if (!this.isValidPoint(dest)) return;        // off the grid
        if (this.isRimEdge(from, dest)) return;      // a segment can't run along the rim

        const input = this.input();
        if (this.isRimPoint(dest) && !this.isRimPoint(from)) {
            // Reaching the rim: toggle this side, then — unless that brings the line
            // home — its wrap-mirror on the far edge too, landing there. When it's a
            // finish, stop on the rim; the mirror would just be an overshoot stray.
            let changed = false;
            if (input?.toggleSegment?.(from, dest)) changed = true;
            if (input?.traceReachesEnd?.()) {
                this.point = dest; // home — stay on the rim, no wrap-mirror
            } else {
                const wrap = this.wrapPoint(dest);
                const farInterior: Position = { row: wrap.row + dRow, col: wrap.col + dCol };
                if (input?.toggleSegment?.(wrap, farInterior)) changed = true;
                this.point = farInterior;
            }
            if (changed) this.cb.onBoardChange();
        } else {
            if (input?.toggleSegment?.(from, dest)) this.cb.onBoardChange();
            this.point = dest;
        }
        this.cb.onChange();
    }

    private move(dRow: number, dCol: number): void {
        if (!this.cursor) return;
        const from = this.cursor;
        let to = { row: from.row + dRow, col: from.col + dCol };

        // Outside a trace, the plain cell cursor wraps toroidally at the edges.
        // (Not while the spacebar is held — a trace stays on-grid — not for rim
        // games, where an edge step means "go to the rim" instead, and not for games
        // that opt out entirely.)
        if (!this.spaceHeld && !this.input()?.allowsRimCursor?.() &&
            (this.input()?.wrapsCellCursor?.() ?? true)) {
            const n = this.cb.getGridSize();
            to = { row: (to.row + n) % n, col: (to.col + n) % n };
        } else if (!this.isValidCursor(to)) {
            return; // blocked by an edge, a rim corner, or a trace boundary
        }

        const moveDir = KeyboardController.dirOf(from, to);
        this.cursor = to;

        if (this.cHeld) {
            // Delete sweep takes precedence: clear the cell we move into. Moving
            // confirms a deliberate hold, so show the red X now (cancel the delay).
            this.showDeleteCursor();
            this.doDeleteAt(to);
        } else if (this.spaceHeld && !this.constrainedMode()) {
            // The first move with space held begins the trace, anchored where the
            // spacebar went down (the cell we're leaving on this first step).
            // (Constrained-trace games drive their trace from stepConstrained.)
            if (!this.tracing) {
                this.tracing = true;
                this.input()?.traceStart?.(from);
            }
            if (this.input()?.traceStep?.({ from, to, moveDir })) {
                this.cb.onBoardChange();
            }
            this.cellTraceFrom = from; // hint: the single last step
        }

        this.cb.onChange();
    }

    /**
     * `X`: send the cursor back to the game's origin — the spot play starts from
     * (see {@link KeyboardControllable.cursorOrigin}). Any gesture under way is
     * dropped first: teleporting the cursor mid-trace would make no sense.
     */
    private jumpToOrigin(): void {
        const input = this.input();
        if (!input) return;
        this.resetTrace();
        this.clearDeleteState();

        if (this.fineMode()) {
            const p = input.fineCursorOrigin?.();
            if (p && this.isValidFine(p)) this.fine = { ...p };
        } else if (this.pointMode()) {
            const p = input.cursorOrigin?.();
            if (p && this.isValidPoint(p)) this.point = { ...p };
        } else {
            const p = input.cursorOrigin?.();
            if (p && this.isValidCursor(p)) this.cursor = { ...p };
        }
        this.cb.onChange();
    }

    private doPlace(): void {
        const input = this.input();
        if (!this.cursor || !input) return;
        if (input.placeAtCursor?.({ ...this.cursor })) this.cb.onBoardChange();
    }

    private doDeleteAt(pos: Position | null): void {
        const input = this.input();
        if (!pos || !input) return;
        if (input.deleteAtCursor?.({ ...pos })) this.cb.onBoardChange();
    }

    /** Delete once at the current cursor (cell, fine, or point, per mode). */
    private deleteAtCursorNow(): void {
        if (this.fineMode()) {
            if (this.fine) this.doDeleteFine(this.fine);
        } else if (this.pointMode()) {
            if (this.point && this.input()?.deleteAtPoint?.({ ...this.point })) this.cb.onBoardChange();
        } else {
            this.doDeleteAt(this.cursor);
        }
    }

    private doPlacePoint(): void {
        const input = this.input();
        if (!this.point || !input) return;
        if (input.placeAtPoint?.({ ...this.point })) this.cb.onBoardChange();
    }

    /** Flip the delete cursor on now, cancelling the tap-delay timer. */
    private showDeleteCursor(): void {
        if (this.cDeleteTimer !== null) {
            window.clearTimeout(this.cDeleteTimer);
            this.cDeleteTimer = null;
        }
        this.cDeleting = true;
    }

    // ── Fine (half-grid) cursor ────────────────────────────────────────

    /** Whether the trace anchors on intersections (Flood) rather than centers (Walls). */
    private fineAnchorsOnIntersections(): boolean {
        return !!this.input()?.tracesFineIntersections?.();
    }

    /** Whether `p` is a point the fine trace can anchor on, for the active game. */
    private isFineAnchorPoint(p: FinePoint): boolean {
        if (this.fineAnchorsOnIntersections()) return KeyboardController.isFineIntersection(p);
        // A rim edge counts for games whose boundary piece anchors there (Branches'
        // stem): a trace can then start *outside* and step in, planting the stem on
        // the way rather than making you tap it first.
        return KeyboardController.isFineCenter(p) || !!this.fineRimCell(p);
    }

    /**
     * The cell just *outside* the grid for a fine point sitting on a rim edge, or
     * null anywhere else (and for games that don't trace from the rim). It is the
     * cell a hop across the rim is reported as coming from, which is what lets the
     * game name the rim edge being crossed.
     */
    private fineRimCell(p: FinePoint): Position | null {
        if (!this.input()?.tracesFromFineRim?.()) return null;
        const hi = 2 * this.cb.getGridSize();
        if (p.r === 0 && p.c % 2 === 1) return { row: -1, col: (p.c - 1) / 2 };
        if (p.r === hi && p.c % 2 === 1) return { row: this.cb.getGridSize(), col: (p.c - 1) / 2 };
        if (p.c === 0 && p.r % 2 === 1) return { row: (p.r - 1) / 2, col: -1 };
        if (p.c === hi && p.r % 2 === 1) return { row: (p.r - 1) / 2, col: this.cb.getGridSize() };
        return null;
    }

    private static isFineCenter(p: FinePoint): boolean {
        return p.r % 2 === 1 && p.c % 2 === 1;
    }
    private static isFineIntersection(p: FinePoint): boolean {
        return p.r % 2 === 0 && p.c % 2 === 0;
    }
    private static fineCell(center: FinePoint): Position {
        return { row: (center.r - 1) / 2, col: (center.c - 1) / 2 };
    }
    private centerFine(): FinePoint {
        const mid = 2 * Math.floor(this.cb.getGridSize() / 2) + 1;
        return { r: mid, c: mid };
    }

    /** Half-grid range. Interior edges only by default; games that place on the
     *  boundary too (Orbits' exit gates) opt into the rim via `allowsFineRim`. */
    private fineBounds(): { lo: number; hi: number } {
        const n = this.cb.getGridSize();
        return this.input()?.allowsFineRim?.()
            ? { lo: 0, hi: 2 * n }
            : { lo: 1, hi: 2 * n - 1 };
    }

    private isValidFine(p: FinePoint): boolean {
        const { lo, hi } = this.fineBounds();
        return p.r >= lo && p.r <= hi && p.c >= lo && p.c <= hi;
    }

    /** Whether the fine cursor runs off one edge and back on the other. On by
     *  default (Walls' block wraps); Orbits opts out — its ship exits instead. */
    private fineWraps(): boolean {
        return this.input()?.wrapsFineCursor?.() ?? true;
    }

    /** On a slot-row rim (Flood's faucet on the top rim), sliding sideways hops to
     *  the next slot midpoint — the odd column that way — skipping the intersection
     *  between. Off that row, or moving vertically, it's an ordinary half-step. */
    private onFineSlotRow(p: FinePoint): boolean {
        return p.r === 0 && !!this.input()?.fineTopRimSlots?.();
    }

    private moveFine(dR: number, dC: number): void {
        if (!this.fine) this.fine = this.centerFine();
        const from = this.fine;
        const { lo, hi } = this.fineBounds();
        const wraps = this.fineWraps();

        if (dR === 0 && this.onFineSlotRow(from)) {
            // Next slot along: one step from an intersection, two from a slot.
            const c = from.c + (from.c % 2 === 1 ? 2 * dC : dC);
            if (c < lo || c > hi) return;
            this.settleFine({ r: from.r, c });
            return;
        }

        let r = from.r + dR;
        let c = from.c + dC;
        if (wraps) {
            if (r < lo) r = hi; else if (r > hi) r = lo;
            if (c < lo) c = hi; else if (c > hi) c = lo;
        } else {
            // No wrap: stop dead at the boundary rather than crossing the grid.
            r = Math.max(lo, Math.min(hi, r));
            c = Math.max(lo, Math.min(hi, c));
            if (r === from.r && c === from.c) return;
        }
        this.settleFine({ r, c });
    }

    /** Land the fine cursor on `to`, applying whatever the held key means there. */
    private settleFine(to: FinePoint): void {
        this.fine = to;

        if (this.cHeld) {
            this.showDeleteCursor();
            this.doDeleteFine(to);
        } else if (this.spaceHeld) {
            this.fineTraceMoved = true; // moved with space held: a trace, not a tap
            this.fineTraceSettle(to);
        }

        this.cb.onChange();
    }

    /**
     * A whole-cell trace move: once the reticle is up (cursor on a center), an
     * arrow jumps straight to the neighbouring cell center — laying the wall —
     * rather than half-stepping through the shared edge. Wraps toroidally: a wrap
     * lays no wall (no edge across the rim) but the block keeps its heading, so
     * the *next* turn still commits on the cell just entered.
     *
     * For a game with hard edges (Orbits) the cursor stays put instead of wrapping —
     * but the hop is still reported, because heading off the grid is exactly the
     * move that places an exit gate. The heading is left alone too, so the trace can
     * carry on from the same cell.
     */
    private moveFineCell(dRow: number, dCol: number): void {
        if (!this.fine) return;
        const n = this.cb.getGridSize();
        // Starting on a rim edge, the hop comes from the notional cell *outside* it,
        // so stepping inward is an ordinary hop that happens to cross the rim.
        const cell = this.fineRimCell(this.fine) ?? KeyboardController.fineCell(this.fine);
        const rawRow = cell.row + dRow;
        const rawCol = cell.col + dCol;
        const wrapped = rawRow < 0 || rawRow >= n || rawCol < 0 || rawCol >= n;
        const moveDir: Direction = dRow < 0 ? 'N' : dRow > 0 ? 'S' : dCol < 0 ? 'W' : 'E';
        if (this.input()?.blocksFineTrace?.(cell, moveDir)) return; // the game refused it
        this.fineTraceMoved = true;

        if (wrapped && !this.fineWraps()) {
            // Off the grid on a hard edge: the cursor holds, but the game still hears
            // the hop — Orbits opens the exit gate the ship would leave through.
            if (this.input()?.traceFine?.({ cell, fromDir: this.fineTraceDir, moveDir })) {
                this.cb.onBoardChange();
            }
            this.cb.onChange();
            return;
        }

        const row = (rawRow + n) % n;
        const col = (rawCol + n) % n;
        const fromCenter = this.fine;
        this.fine = { r: 2 * row + 1, c: 2 * col + 1 };

        if (!wrapped) {
            if (this.input()?.traceFine?.({ cell, fromDir: this.fineTraceDir, moveDir })) {
                this.cb.onBoardChange();
            }
        }
        // Either way the block is now heading moveDir; the hint spans the hop. On
        // a wrap it comes from one cell *outside* the entry edge (the block wrapped
        // in from there), rather than the far center it actually left.
        this.fineTraceDir = moveDir;
        this.fineTraceFrom = wrapped
            ? { r: this.fine.r - 2 * dRow, c: this.fine.c - 2 * dCol }
            : fromCenter;
        this.cb.onChange();
    }

    /**
     * A trace hop between adjacent **intersections** (Flood): the cursor jumps the
     * two half-steps to the next intersection and the game acts on the edge crossed
     * in between — the half-grid point midway. The boundary simply stops the hop.
     */
    private moveFineIntersection(dRow: number, dCol: number): void {
        if (!this.fine) return;
        if (this.input()?.tracesToroidal?.()) { this.moveFineToroidal(dRow, dCol); return; }

        const from = this.fine;
        const to: FinePoint = { r: from.r + 2 * dRow, c: from.c + 2 * dCol };
        if (!this.isValidFine(to)) return;

        const edge: FinePoint = { r: from.r + dRow, c: from.c + dCol };
        const moveDir: Direction = dRow < 0 ? 'N' : dRow > 0 ? 'S' : dCol < 0 ? 'W' : 'E';
        this.fine = to;
        this.fineTraceMoved = true;

        const input = this.input();
        // A game acts either on what it crossed (Flood) or on where it landed (Deluge).
        if (input?.traceFineEdge?.(edge, moveDir)) this.cb.onBoardChange();
        if (input?.traceFinePoint?.({ ...to })) this.cb.onBoardChange();
        this.fineTraceDir = moveDir;
        this.fineTraceFrom = from; // only drawn if the game asked for the hint
        this.cb.onChange();
    }

    /**
     * A toroidal intersection trace (Line), on the half-grid so the cursor can also
     * rest on edges to tap them. Each hop toggles the segment crossed; reaching the
     * rim also toggles its wrap-mirror on the far edge and lands there, so a wrapping
     * line is drawn in one sweep — unless the hop brings the line home
     * ({@link KeyboardControllable.traceReachesEnd}), where it stops on the rim.
     * Works in intersection `Position`s (fine `{2·row, 2·col}`), like the old point trace.
     */
    private moveFineToroidal(dRow: number, dCol: number): void {
        const input = this.input();
        const from: Position = { row: this.fine!.r / 2, col: this.fine!.c / 2 };
        const dest: Position = { row: from.row + dRow, col: from.col + dCol };
        if (!this.isValidPoint(dest)) return;   // off the grid
        if (this.isRimEdge(from, dest)) return; // a segment can't run along the rim

        this.fineTraceMoved = true;
        let changed = false;
        let land: Position;
        if (this.isRimPoint(dest) && !this.isRimPoint(from)) {
            // A rim crossing. Whether it wraps (toggle the mirror on the far edge and
            // land there) or stops on the rim depends on what the toggle *did* — not on
            // whether the line happens to reach home right now, which stays true across
            // a whole won board and would wrongly force every crossing to stop.
            const existed = !!input?.hasSegment?.(from, dest);
            const wasEnd = !!input?.traceReachesEnd?.();
            if (input?.toggleSegment?.(from, dest)) changed = true;
            const nowEnd = !!input?.traceReachesEnd?.();

            const wrap = this.wrapPoint(dest);
            const far: Position = { row: wrap.row + dRow, col: wrap.col + dCol };

            if (!wasEnd && nowEnd) {
                land = dest; // this crossing just brought the line home — stop on the rim
            } else if (existed) {
                // Erasing the crossing: drop its wrap-mirror too if present, add none.
                // Undoing a wrap has to put the cursor back where the wrap took it
                // from, so it lands on the far interior point exactly when the mirror
                // went with it — retracing the hop step for step. With no mirror to
                // erase, nothing happened over there and the cursor stays on the rim.
                const mirrored = !!input?.hasSegment?.(wrap, far) && !!input?.toggleSegment?.(wrap, far);
                if (mirrored) changed = true;
                land = mirrored ? far : dest;
            } else {
                // Extending across the rim mid-line: the mirror continues it on the far edge.
                land = far;
                if (input?.toggleSegment?.(wrap, far)) changed = true;
            }
        } else {
            if (input?.toggleSegment?.(from, dest)) changed = true;
            land = dest;
        }

        this.fine = { r: 2 * land.row, c: 2 * land.col };
        this.fineTraceDir = dRow < 0 ? 'N' : dRow > 0 ? 'S' : dCol < 0 ? 'W' : 'E';
        this.fineTraceFrom = null; // no arrow hint (a segment has no single heading)
        if (changed) this.cb.onBoardChange();
        this.cb.onChange();
    }

    /** Anchor the fine trace at `p`, telling the game so it can track its own start
     *  (Deluge's snap anchor). */
    private anchorFineTrace(p: FinePoint): void {
        this.fineTraceDir = null;
        this.fineTraceFrom = null;
        this.fineTraceActive = true;
        this.input()?.traceFineStart?.({ ...p });
    }

    /**
     * Half-step arrival during a fine trace (before the reticle is up): landing on an
     * anchor point (a cell center, or an intersection for games that trace along the
     * lines) starts a fresh trace there with no known heading; landing on the *other*
     * kind interrupts it; an edge is just transit.
     */
    private fineTraceSettle(to: FinePoint): void {
        if (this.isFineAnchorPoint(to)) {
            this.anchorFineTrace(to);
            return;
        }
        const isOtherAnchor = this.fineAnchorsOnIntersections()
            ? KeyboardController.isFineCenter(to)
            : KeyboardController.isFineIntersection(to);
        if (!isOtherAnchor) return; // transiting an edge

        this.fineTraceActive = false;
        this.fineTraceDir = null;
        this.fineTraceFrom = null;
    }

    private doPlaceFine(): void {
        const input = this.input();
        if (!this.fine || !input) return;
        if (input.placeAtFine?.({ ...this.fine })) this.cb.onBoardChange();
    }

    private doDeleteFine(p: FinePoint): void {
        const input = this.input();
        if (!input) return;
        if (input.deleteAtFine?.({ ...p })) this.cb.onBoardChange();
    }

    /** True when the event targets a text field, so we never steal typing. */
    private isTypingTarget(target: EventTarget | null): boolean {
        const el = target as HTMLElement | null;
        if (!el || !el.tagName) return false;
        return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable;
    }

    private handleKeyDown = (e: KeyboardEvent): void => {
        if (this.isTypingTarget(e.target)) return;
        // Never hijack browser/OS shortcuts (Ctrl+C, Cmd+K, Alt+…).
        if (e.ctrlKey || e.metaKey || e.altKey) return;
        if (!this.cb.isInGame()) return;

        const key = e.key.toLowerCase();

        // K toggles keyboard mode regardless of current state.
        if (key === 'k') {
            this.setEnabled(!this.enabled);
            this.cb.onToggle(this.enabled);
            e.preventDefault();
            return;
        }

        if (!this.enabled) return;

        switch (key) {
            // Up: arrow, W (QWERTY), Z (AZERTY)
            case 'arrowup': case 'w': case 'z':
                this.step(-1, 0); e.preventDefault(); break;
            // Down: arrow, S (both layouts)
            case 'arrowdown': case 's':
                this.step(1, 0); e.preventDefault(); break;
            // Left: arrow, A (QWERTY), Q (AZERTY)
            case 'arrowleft': case 'a': case 'q':
                this.step(0, -1); e.preventDefault(); break;
            // Right: arrow, D (both layouts)
            case 'arrowright': case 'd':
                this.step(0, 1); e.preventDefault(); break;
            case ' ':
                // Tap → place on key-up; hold-and-move → trace (begun on the first
                // move so a tap never triggers a trace).
                if (!this.spaceHeld) {
                    this.spaceHeld = true;
                    this.tracing = false;
                    // Fine cursor: starting a trace on a cell center shows the
                    // reticle immediately, anchored there.
                    if (this.fineMode() && this.fine && this.isFineAnchorPoint(this.fine)) {
                        this.anchorFineTrace(this.fine);
                        this.cb.onChange();
                    } else if (this.constrainedMode()) {
                        // Anchor at once if the cell is valid and nothing is pending;
                        // otherwise wait (a pending pair completes on release instead).
                        this.ctActive = false;
                        this.ctAnchor = null;
                        this.tryAnchor();
                    }
                }
                e.preventDefault();
                break;
            case 'c':
                // Ignore the OS key-repeat; act only on the genuine first press.
                if (!e.repeat && !this.cHeld) {
                    this.cHeld = true;
                    this.deleteAtCursorNow(); // delete the current spot at once
                    // Delay the red X so a quick tap-to-delete doesn't flash it.
                    this.cDeleteTimer = window.setTimeout(() => {
                        this.cDeleteTimer = null;
                        this.cDeleting = true;
                        this.cb.onChange();
                    }, DELETE_CURSOR_DELAY_MS);
                }
                e.preventDefault();
                break;
            case 'x':
                this.jumpToOrigin(); e.preventDefault(); break;
            // (`R` resets the game, which sends the cursor to the origin via
            //  resetCursor — see the app's reset path.)
            case 'v':
                this.cb.onToggleGateMode(); e.preventDefault(); break;
            case 'h':
                this.cb.onToggleHub(); e.preventDefault(); break;
            case 'b':
                this.cb.onRestoreBest(); e.preventDefault(); break;
            case 'n':
                this.cb.onNextGame(); e.preventDefault(); break;
            case 'r':
                this.cb.onReset(); e.preventDefault(); break;
        }
    };

    private handleKeyUp = (e: KeyboardEvent): void => {
        if (!this.enabled) return;
        if (this.isTypingTarget(e.target)) return;

        if (e.key === ' ') {
            e.preventDefault();
            if (!this.spaceHeld) return;
            if (this.constrainedMode()) { this.endConstrainedTrace(); return; }
            // A trace is a hold that *moved*. In fine mode the reticle goes up on
            // space-down, so `fineTraceActive` alone would swallow a plain tap.
            const wasTracing = this.fineMode() ? this.fineTraceMoved : this.tracing;
            // …but a fine trace also *anchors* on space-down, telling the game via
            // `traceFineStart`. Whatever it put up then has to come down even if the
            // gesture turned out to be a tap (Deluge's snap dots).
            const wasAnchored = this.fineTraceActive;
            this.resetTrace();

            // Close out any gesture the game heard the start of: it commits what the
            // trace built (Antennas' emitter) or tidies up after it (Deluge's anchor).
            if ((wasTracing || wasAnchored) && this.input()?.traceEnd?.()) {
                this.cb.onBoardChange();
            }

            if (!wasTracing) {
                if (this.fineMode()) this.doPlaceFine();       // a tap on an edge toggles a wall
                else if (this.pointMode()) this.doPlacePoint(); // a tap on a rim point sets the start
                else this.doPlace();                            // a tap places at the cursor cell
            }
            // Always re-render: the reticle goes up on space-*down*, so it has to come
            // back down even when the release changed nothing (a tap on an
            // intersection, where there's no edge to place on).
            this.cb.onChange();
            return;
        }

        if (e.key.toLowerCase() === 'c') {
            e.preventDefault();
            if (!this.cHeld) return;
            const wasDeleting = this.cDeleting;
            this.clearDeleteState();
            // Only re-render if the red X was actually showing (a hold); a quick
            // tap already re-rendered via its delete and never showed the X.
            if (wasDeleting) this.cb.onChange();
        }
    };
}
